"use client";

import { useEffect, useState } from "react";
import { List } from "lucide-react";
import { cn } from "@/lib/utils";

export type TocEntry = { id: string; label: string };

/** Sticky-Inhaltsverzeichnis mit Scrollspy und sanftem Scrollen. */
export default function TableOfContents({ entries }: { entries: TocEntry[] }) {
  const [activeId, setActiveId] = useState<string>(entries[0]?.id ?? "");

  useEffect(() => {
    const headings = entries
      .map((entry) => document.getElementById(entry.id))
      .filter((el): el is HTMLElement => el !== null);
    if (!headings.length) return;

    const observer = new IntersectionObserver(
      (observed) => {
        const visible = observed
          .filter((entry) => entry.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
        if (visible[0]) setActiveId(visible[0].target.id);
      },
      { rootMargin: "-96px 0px -65% 0px", threshold: 0 }
    );
    headings.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, [entries]);

  function scrollTo(e: React.MouseEvent<HTMLAnchorElement>, id: string) {
    e.preventDefault();
    const el = document.getElementById(id);
    if (!el) return;
    el.scrollIntoView({ behavior: "smooth", block: "start" });
    history.replaceState(null, "", `#${id}`);
    setActiveId(id);
  }

  if (!entries.length) return null;

  return (
    <nav
      aria-label="Inhaltsverzeichnis"
      className="card-flat bg-white/70 p-5"
    >
      <p className="flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-ink-faint mb-4">
        <List size={13} aria-hidden="true" />
        Inhalt
      </p>
      <ul className="flex flex-col gap-0.5">
        {entries.map((entry, i) => {
          const active = activeId === entry.id;
          return (
            <li key={entry.id}>
              <a
                href={`#${entry.id}`}
                onClick={(e) => scrollTo(e, entry.id)}
                className={cn(
                  "group flex items-start gap-2.5 rounded-lg px-2.5 py-2 text-sm leading-snug transition-colors duration-150",
                  active
                    ? "bg-accent-soft text-accent font-semibold"
                    : "text-ink-soft hover:text-ink hover:bg-paper-deep"
                )}
              >
                <span
                  className={cn(
                    "tnum shrink-0 text-xs font-bold mt-0.5 transition-colors",
                    active ? "text-accent" : "text-ink-faint group-hover:text-ink-soft"
                  )}
                >
                  {String(i + 1).padStart(2, "0")}
                </span>
                {entry.label}
              </a>
            </li>
          );
        })}
      </ul>
    </nav>
  );
}
